import { defineStore } from 'pinia'
import { ref } from 'vue'
import { shopInfoById } from '@/api/shops'
import { getSettings, setLanguageSetting, setLogoUrl, setReportTimezone, setSubscriptionUrl, updateCardsSetting, updatePaypalSetting, updateWalletsSetting, updateStorefrontAccessToken } from '@/api/settings'
import type { ShopEntity } from '@/types/payment-gateway-config'
import type { ShopAPI } from '@/api/typeings/shops'

export const useShopStore = defineStore('shop', () => {
  interface ShopDetail {
    id: number
    shopName: string
    shopifyId: string
    [key: string]: any
  }
  const shopId = ref('')
  const shopState = ref(false)
  const shopStateMsg = ref('')
  const shopDetail = ref<ShopDetail>({
    id: 0,
    shopName: '',
    shopifyId: '',
  })

  const originalData = ref<ShopAPI.ShopDto>({} as ShopAPI.ShopDto)

  function normalizeShopifyAppShopDetail(input: any): Partial<ShopDetail> | null {
    if (!input || typeof input !== 'object') return null
    // /shopify-app/shop 返回的 data 典型包含 installed/installation/requestedScopes/grantedScopes/shop/shopifyId
    if (!('installed' in input) && !('installation' in input) && !('shop' in input) && !('shopifyId' in input)) {
      return null
    }

    const shop = input.shop ?? {}
    const shopifyId =
      input.shopifyId ??
      input.shopify_id ??
      (typeof shop?.myshopifyDomain === 'string' ? shop.myshopifyDomain.split('.')[0] : undefined) ??
      (typeof shop?.myshopify_domain === 'string' ? shop.myshopify_domain.split('.')[0] : undefined) ??
      ''

    return {
      // 保持兼容：ShopEntity/ShopDto 的 id 仍然是 number；Shopify 的 GID 另存
      id:
        typeof input.id === 'number'
          ? input.id
          : typeof shopDetail.value.id === 'number'
            ? shopDetail.value.id
            : 0,
      shopGid: shop?.id ?? input.shopGid ?? shopDetail.value.shopGid,
      shopName: shop?.name ?? input.shopName ?? input.shop_name ?? shopDetail.value.shopName ?? '',
      shopifyId: String(shopifyId || shopDetail.value.shopifyId || ''),
      domain: shop?.domain ?? shop?.myshopifyDomain ?? shop?.myshopify_domain ?? input.domain ?? shopDetail.value.domain,
      installed: input.installed,
      installation: input.installation,
      requestedScopes: input.requestedScopes,
      grantedScopes: input.grantedScopes,
      shop,
    }
  }

  // actions
  const fetchShopDetail = async () => {
    try {
      if (!shopId.value) {
        throw new Error('Missing shopId')
      }
      // 新接口：聚合 settings + shop
      const [shopData, settingsData] = await Promise.all([
        shopInfoById(shopId.value || ''),
        getSettings(),
      ])

      const shop: any = (shopData as any)?.shop ?? {}
      const settings: any = settingsData as any
      // GET /shopify-app/setting 里 integrations 拼写为 intergrations，与 integrations 页回填一致
      const integrationsPayload = settings?.intergrations ?? settings?.integrations
      const intergrationsStr =
        integrationsPayload != null && typeof integrationsPayload === 'object'
          ? JSON.stringify(integrationsPayload)
          : typeof integrationsPayload === 'string'
            ? integrationsPayload
            : undefined

      // 兼容旧页面逻辑：仍然提供 shopDetail.paypalApi / ccApi 等字符串字段
      const merged: any = {
        id: shop?.id ?? 0,
        shopName: shop?.name ?? '',
        shopifyId: (shopData as any)?.shopifyId ?? shopId.value,
        domain: shop?.domain ?? shop?.myshopifyDomain ?? shop?.myshopify_domain,
        settings: JSON.stringify({
          logo: settings?.logo?.url ?? null,
          language: settings?.locale?.language ?? 'English',
          isEnableConversionAPI: Boolean(settings?.locale?.isEnableConversionAPI ?? false),
          reportTimezone: settings?.locale?.reportTimezone ?? '(GMT-11:00) Midway Islands Time',
          forbiddenCountries: settings?.locale?.forbiddenCountries ?? [],
          subscriptionUrl: settings?.subscription?.subscriptionUrl ?? '',
        }),
        paypalApi: JSON.stringify(settings?.payments?.paypal ?? {}),
        ccApi: JSON.stringify(settings?.payments?.cards ?? {}),
        walletApi: JSON.stringify(settings?.payments?.wallets ?? {}),
        storefrontApiAccessToken: settings?.storefront?.storefrontApiAccessToken,
        storefrontPrivateToken: settings?.storefront?.storefrontPrivateToken,
        ...(intergrationsStr != null ? { intergrations: intergrationsStr } : {}),
      }

      originalData.value = merged as any
      shopDetail.value = {
        ...merged,
        id: String(merged.id ?? ''),
      }

      return merged
    } catch (error: any) {
      console.error('Error fetching shop detail:', error)
      throw error
    }
  }

  const updateShop = async (params: ShopAPI.ShopDto) => {
    try {
      // 将旧的 shopUpdate 模式拆分为新接口调用
      // 1) settings: logo/language/timezone/subscriptionUrl
      if ((params as any)?.settings) {
        try {
          const settings = JSON.parse(String((params as any).settings || '{}'))
          if (settings.logo !== undefined && settings.logo !== null) {
            await setLogoUrl(String(settings.logo))
          }
          if (settings.language !== undefined || settings.isEnableConversionAPI !== undefined) {
            await setLanguageSetting({
              language: String(settings.language ?? 'English')
            })
          }
          if (settings.reportTimezone) {
            await setReportTimezone(String(settings.reportTimezone))
          }
          if (settings.subscriptionUrl) {
            await setSubscriptionUrl(String(settings.subscriptionUrl))
          }
        } catch (e) {
          // ignore parse errors
        }
      }

      // 2) payment configs
      if ((params as any)?.paypalApi) {
        const paypal = JSON.parse(String((params as any).paypalApi || '{}'))
        await updatePaypalSetting(paypal)
      }
      if ((params as any)?.ccApi) {
        const cards = JSON.parse(String((params as any).ccApi || '{}'))
        await updateCardsSetting(cards)
      }
      if ((params as any)?.walletApi) {
        const wallets = JSON.parse(String((params as any).walletApi || '{}'))
        await updateWalletsSetting(wallets)
      }

      // 3) storefront token
      if ((params as any)?.storefrontApiAccessToken) {
        const privateToken = (params as any)?.storefrontPrivateToken
        await updateStorefrontAccessToken(
          String((params as any).storefrontApiAccessToken),
          privateToken ? String(privateToken) : '',
        )
      }

      await fetchShopDetail()
      return { success: true, message: '更新成功', data: true } as any
    } catch (error: any) {
      console.error('Error updating shop:', error)
      throw error
    }
  }

  const setShopDetail = (newDetail: Partial<ShopEntity>) => {
    // 兼容直接把 /shopify-app/shop 的 data 写进 store 的场景
    const normalized = normalizeShopifyAppShopDetail(newDetail as any)
    shopDetail.value = {
      ...shopDetail.value,
      ...(normalized ?? (newDetail as any)),
    }
  }

  const setShopId = (id: string) => {
    shopId.value = id
  }

  const setShopState = (state: boolean, msg?: string) => {
    shopState.value = state
    shopStateMsg.value = msg || ''
  }

  return {
    // state
    shopId,
    setShopId,
    shopDetail,
    originalData,
    shopState,
    shopStateMsg,
    setShopState,
    // actions
    fetchShopDetail,
    updateShop,
    setShopDetail,
  }
}, {
  persist: true
})
